<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import { registerFocus } from '../../focus-manager/focus.action.svelte.js';
  import type { FocusItem, FocusLayer } from '../../focus-manager/focus-types';

  /**
   * An arbitrary value that is represented by this menu item.
   *
   * This value is passed in the `selectItem` event triggered on selection of
   * this menu item.
   */
  export let value: unknown;

  /**
   * Whether this menu item is checked. The value is used for accessibility
   * purposes as well as for showing a visual indicator.
   */
  export let checked = false;

  export let focusLayer: FocusLayer;
  export let focusName: FocusItem;

  const dispatch = createEventDispatcher();

  const selectItem = () => {
    dispatch('selectItem', value);
  };
</script>

<button
  type="button"
  role="menuitemradio"
  class="menu-item-btn"
  tabindex="-1"
  aria-checked={checked}
  on:click={selectItem}
  data-focusName={focusName}
  data-focusLayer={focusLayer}
  use:registerFocus={{ focusName, focusLayer }}
>
  <slot />
  <svg
    class="checkmark"
    width="14"
    height="12"
    viewBox="0 0 14 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M4.5 9.34717L3.61613 8.4633L0.75 5.59648V7.66667L3.61613 10.519L4.5 11.4029L5.38387 10.519L13.25 2.6529V0.597168L5.38387 8.4633L4.5 9.34717Z"
      fill="currentColor"
    />
  </svg>
</button>

<style module>
  [role='menuitemradio'].menu-item-btn {
    width: 100%;
    min-width: 2.75em;
    min-height: 2.75em;
    padding: 0 0.5em;
    white-space: nowrap;
    border: none;
    background: none;
    color: inherit;
    font-size: clamp(0.75em, 1cqw + 0.5em, 1em); /* Fallback for older browsers is the initial value of 1em */
    text-align: left;
  }

  [role='menuitemradio'].menu-item-btn .checkmark {
    display: none;
  }

  [role='menuitemradio'][aria-checked='true'].menu-item-btn .checkmark {
    display: inline-block;
    margin-left: 2em;
  }

  [role='menuitemradio'][aria-checked='true'].menu-item-btn {
    font-weight: 700;
  }

  [role='menuitemradio'].menu-item-btn:hover {
    background-color: #333333;
    background-color: var(--menu-item-hover-background-color);
  }

  [role='menuitemradio'].menu-item-btn:focus {
    outline: 2px solid #ffffff; /* Fallback for older browsers */
    outline: var(--outline);
  }

  @supports selector(:focus-visible) {
    /* Don't show the outline on focus... */
    [role='menuitemradio'].menu-item-btn:focus {
      outline: none;
    }

    /* ...but show it on focus-visible */
    [role='menuitemradio'].menu-item-btn:focus-visible {
      outline: 2px solid #ffffff; /* Fallback for older browsers */
      outline: var(--outline);
    }
  }

  [role='menuitemradio'].menu-item-btn:enabled {
    cursor: pointer;
  }

  @media (prefers-reduced-motion: no-preference) {
    [role='menuitemradio'].menu-item-btn {
      transition: background-color 0.3s ease-in-out;
    }

    [role='menuitemradio'].menu-item-btn::before {
      transition: background-color 0.3s ease-in-out;
    }
  }
</style>
